import type { ListingAgentResearchRunDto, ListingAgentResearchTrailEntryDto } from "@/api/listingAgentResearch";
import { StatusBadge, Timestamp } from "@/design-system/components/ui";
import { formatCurrency } from "@/design-system/lib/format";
import { DetailSection } from "../../detailBits";
import {
  RUN_STATUS,
  STEP_LABEL,
  TRAIL_KIND_LABEL,
  agentResearchCopy,
  terminalReasonText,
} from "./agentResearchCopy";
import { SafeLink } from "./StartStage";

const cents = (v: number) => formatCurrency(v, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** A run's cost is a fraction of a cent, so it shows to three decimals
 *  ("$0.004"); a positive cost that rounds to "$0.000" shows as "<$0.01". */
export function runCostText(v: number): string {
  const text = formatCurrency(v, { minimumFractionDigits: 3, maximumFractionDigits: 3 });
  return v > 0 && text === formatCurrency(0, { minimumFractionDigits: 3, maximumFractionDigits: 3 })
    ? `<${cents(0.01)}`
    : text;
}

/**
 * ② AI research (D12) — the run's live status and its trail. Re-read every five
 * seconds while the run is live (the page's run query). A failed or stopped run
 * still shows everything it did and found; "AI classification" entries are
 * marked as the model's judgement (R19).
 */
export function ResearchStage({ run }: { run: ListingAgentResearchRunDto | null | undefined }) {
  if (!run) {
    return (
      <p className="text-small text-gray-mid m-0" data-testid="agent-research-no-run">
        {agentResearchCopy.noRunYet}
      </p>
    );
  }

  const status = RUN_STATUS[run.status] ?? { label: run.status, tone: "neutral" as const };
  const reason = terminalReasonText(run.terminalReason);
  const u = run.usage;
  const trail = [...run.trail].sort((a, b) => a.sequence - b.sequence);

  return (
    <div className="grid gap-space-5" data-testid="agent-research-research">
      <DetailSection title="Status">
        <div className="grid gap-space-2">
          <div className="flex flex-wrap items-center gap-space-2">
            <StatusBadge tone={status.tone} data-testid="agent-research-run-status">
              {status.label}
            </StatusBadge>
            <span className="text-caption text-gray-mid">
              Started by {run.requestedByName ?? "a colleague"} · <Timestamp value={run.queuedAt} />
            </span>
          </div>
          {run.isActive && (
            <p className="text-small text-gray-mid m-0" data-testid="agent-research-close-page">
              {agentResearchCopy.closeThisPage}
            </p>
          )}
          {!run.isActive && reason && (
            <p className="text-small text-onyx m-0" data-testid="agent-research-terminal-reason">
              {reason}
            </p>
          )}
          {run.error && (
            <p className="text-small text-status-error m-0" data-testid="agent-research-error">
              {run.error}
            </p>
          )}
          <p className="text-caption text-gray-mid m-0" data-testid="agent-research-usage">
            Searches {u.searchesUsed} of {u.maxSearches} · Pages read {u.fetchesUsed} of {u.maxFetches} · AI calls{" "}
            {u.modelCallsUsed} of {u.maxModelCalls} · Cost {runCostText(u.estimatedCostUsd)} of {cents(u.costCapUsd)} · Up
            to {u.maxMinutes} minutes
          </p>
          {run.inputNotes && (
            <p className="text-caption text-gray-mid m-0">Started with your notes: {run.inputNotes}</p>
          )}
        </div>
      </DetailSection>

      <DetailSection title="What the research did">
        {trail.length === 0 ? (
          <p className="text-small text-gray-mid m-0">Nothing yet — the run is about to start.</p>
        ) : (
          <ol className="m-0 grid list-none gap-space-2 p-0" data-testid="agent-research-trail">
            {trail.map((e) => (
              <TrailEntry key={e.sequence} entry={e} />
            ))}
          </ol>
        )}
      </DetailSection>
    </div>
  );
}

function TrailEntry({ entry: e }: { entry: ListingAgentResearchTrailEntryDto }) {
  const isAi = e.kind === "AiClassification" || e.label === "AI classification" || e.label === "AI note";
  return (
    <li
      className="grid gap-space-1 rounded-md border border-border-light bg-white-pure px-space-3 py-space-2"
      data-testid={`agent-research-trail-${e.sequence}`}
    >
      <div className="flex flex-wrap items-center gap-space-2">
        <span className="text-caption uppercase tracking-wide text-gray-mid">{STEP_LABEL[e.step] ?? e.step}</span>
        <span className="text-small font-medium text-onyx">{TRAIL_KIND_LABEL[e.kind] ?? e.kind}</span>
        {isAi && (
          <StatusBadge tone="info" data-testid="agent-research-trail-ai">
            {agentResearchCopy.aiJudgement}
          </StatusBadge>
        )}
        {e.label && !isAi && (
          <StatusBadge tone="neutral" data-testid="agent-research-trail-label">
            {e.label}
          </StatusBadge>
        )}
        <span className="ml-auto text-caption text-gray-mid">
          <Timestamp value={e.occurredAtUtc} />
        </span>
      </div>
      {e.query && <p className="text-small text-onyx m-0">Searched for “{e.query}”</p>}
      {e.url && (
        <p className="text-small m-0">
          <SafeLink url={e.url} />
        </p>
      )}
      {e.outcome && <p className="text-small text-onyx m-0">{e.outcome}</p>}
      {e.detail && <p className="text-caption text-gray-mid m-0">{e.detail}</p>}
    </li>
  );
}
